import React from 'react';
import ReactDOM from 'react-dom';
import { DiastasisRectiVideo } from '../../components/DiastasisRectiVideo/DiastasisRectiVideo';
import { SurveyOptions, SurveySender } from '../../types/surveys';

export const handleVideoInjection = (pageName: string) =>
  requestAnimationFrame(() => {
    const pagesWithVideo = ['DR Check'];
    const pageNeedsVideo = pagesWithVideo.includes(pageName);

    const questionContainer = document.querySelector('.sv_q');
    const buttonContainer = questionContainer?.lastChild;

    if (pageNeedsVideo && questionContainer && buttonContainer) {
      // Create a container for the injected video
      const videoContainer = document.createElement('div');
      videoContainer.className = 'injected-video-container';

      questionContainer.insertBefore(videoContainer, buttonContainer);
      ReactDOM.render(<DiastasisRectiVideo isDisabled={false} />, videoContainer);
    } else {
      const existingVideo = document.querySelector('.injected-video-container');
      if (existingVideo) {
        ReactDOM.unmountComponentAtNode(existingVideo);
        existingVideo.remove();
      }
    }
  });

export const createBulletListFromDescription = (pageName: string) =>
  requestAnimationFrame(() => {
    const pagesWithBulletList = ['OPF Symptoms', 'Prolapse Symptoms'];
    if (!pagesWithBulletList.includes(pageName)) {
      return;
    }

    const descriptionContainer: Element | null = document.querySelector('.sv_q_description');
    const descriptionSpan: Element | null = descriptionContainer
      ? descriptionContainer.querySelector('.sv-string-viewer')
      : null;

    const newDescriptionListElement: Element = document.createElement('ul');

    descriptionSpan?.innerHTML?.split('\n').forEach(value => {
      const element = document.createElement('li');
      element.innerHTML = value;
      newDescriptionListElement.appendChild(element);
    });

    if (descriptionSpan && descriptionContainer?.contains(descriptionSpan)) {
      descriptionContainer?.removeChild(descriptionSpan);
      descriptionContainer?.appendChild(newDescriptionListElement);
    }
  });

export const enableNextButton = (options: SurveyOptions) => {
  // Use requestAnimationFrame to ensure DOM is fully rendered
  requestAnimationFrame(() => {
    const validQuestionsWithButtons = ['checkbox', 'comment', 'text'];
    const availableNextButtons = document.querySelectorAll('.sv_next_btn');
    const availableCompleteButtons = document.querySelectorAll('.sv_complete_btn');

    const wasPreviouslyAnswered = Object.keys(options.page.getValue()).length > 0;
    const questionType: string = options.page.questions[0].getType();

    const buttonsToShow =
      availableCompleteButtons.length > 0 ? availableCompleteButtons : availableNextButtons;

    if (buttonsToShow.length === 2) {
      const bottomButton = buttonsToShow[1] as HTMLInputElement;

      if (validQuestionsWithButtons.includes(questionType) || wasPreviouslyAnswered) {
        bottomButton.style.display = 'block';
      } else {
        bottomButton.style.display = 'none';
      }

      if (wasPreviouslyAnswered) {
        bottomButton.classList.add('sv_next_btn_active');
      } else {
        bottomButton.classList.remove('sv_next_btn_active');
      }

      // Override continue label to Next
      bottomButton.value = 'Next';
    }
  });
};

export const handleMenopausePage = (sender: SurveySender, options: SurveyOptions) => {
  const questionContainer: HTMLElement | null = document.querySelector('.sv_container');
  const rootContainer: HTMLElement | null = questionContainer
    ? questionContainer.querySelector('.sv_p_root')
    : null;

  if (options.page.propertyHash.name === 'Menopause') {
    const menopauseTitleElement = document.createElement('h5');

    questionContainer?.classList.add('menopause_container');
    menopauseTitleElement.classList.add('sv_q_title', 'menopause_title');
    menopauseTitleElement.textContent = options.page.title;
    rootContainer?.parentNode?.insertBefore(menopauseTitleElement, rootContainer);
  } else {
    const menopauseTitleElement = questionContainer?.querySelector('.menopause_title');
    if (menopauseTitleElement) {
      menopauseTitleElement.remove();
    }

    questionContainer?.classList.remove('menopause_container');
  }
};

export const handleTopMargin = (addMargin: boolean) => {
  const header: Element | null = document.querySelector('.sv_header__text');
  if (header && addMargin) {
    header.classList.add('top_margin');
  } else if (header && !addMargin) {
    header.classList.remove('top_margin');
  }
};

export const injectChoiceDescriptions = (sender: SurveySender) =>
  requestAnimationFrame(() => {
    // Find the activity_level question container
    const activityLevelContainer = document.querySelector('[data-name="activity_level"]');
    if (!activityLevelContainer) {
      return;
    }

    // Get the question from the survey model
    const { currentPage } = sender;
    const activityQuestion = currentPage.elements.find(q => q.name === 'activity_level');

    if (!activityQuestion || !activityQuestion.choices) {
      return;
    }

    // Find all radio button containers
    const radioGroups = activityLevelContainer.querySelectorAll('.sv_q_radiogroup');

    // Inject descriptions for each choice
    radioGroups.forEach((radioGroup, index) => {
      const choice = activityQuestion.choices[index];

      // Check if description already exists to avoid duplicates
      const existingDescription = radioGroup.querySelector('.choice-description');

      // Access description from SurveyJS ItemValue's jsonObj property
      const description = choice.jsonObj?.description;

      if (existingDescription || !description) {
        return;
      }

      // Create description element
      const descriptionElement = document.createElement('div');
      descriptionElement.className = 'choice-description';
      descriptionElement.textContent = description;

      // Insert description before the radio button label (as first child)
      radioGroup.insertBefore(descriptionElement, radioGroup.firstChild);
    });
  });
